/**
 * The SDMS "sheet not ready" panel (user 2026-09-28: "the user must know clearly that the sheet is not
 * yet ready to enter or view the data"; layout = option 4, minimal). Covers the whole sheet area — solid,
 * so no empty grid or half-drawn values show behind it — from Retrieve until the calculation has settled
 * (and, on an engine-bound template, the on-open server compute): the shared loader, the current step,
 * "Step N of 3 · please wait" and ONE progress bar with ONE overall percent across all three steps (user
 * 2026-09-28: "a single progress with percentage that covers the entire three steps"). The only animation is
 * the loader's spin, which is transform-driven: building and calculating the sheet keep the browser's main
 * thread busy for long stretches, and anything animated there (a sliding bar, a seconds counter) froze. The
 * page disables the sheet actions meanwhile.
 */
import { useRef } from 'react';
import { LoaderSymbol } from '../../components/loading';

/** Where the load is: fetching the batch data, building the workbook, calculating its results. */
export type SheetPrepStage = 'data' | 'build' | 'calc';

const STAGE_LABEL: Record<SheetPrepStage, string> = {
  data: 'Loading batch data',
  build: 'Building the sheet',
  calc: 'Calculating results',
};
const STAGE_NO: Record<SheetPrepStage, number> = { data: 1, build: 2, calc: 3 };

/** Each step's share of the overall bar, weighted by where an open really spends its time (VOC
 *  QB26092801_001: ~26 s loading and re-merging the data, ~1 s building, ~5 s calculating). */
const STAGE_SPAN: Record<SheetPrepStage, readonly [number, number]> = {
  data: [0, 70],
  build: [70, 80],
  calc: [80, 100],
};

/** The overall percent (0–100) for a step and that step's own percent (null = not counted yet → the step's start). */
export function overallPct(stage: SheetPrepStage, pct: number | null): number {
  const [from, to] = STAGE_SPAN[stage];
  const within = Math.max(0, Math.min(100, pct ?? 0));
  return Math.round(from + ((to - from) * within) / 100);
}

export interface SdmsSheetPreparingProps {
  stage: SheetPrepStage;
  /** The current step's OWN percent — step 1 by its finished sub-steps, step 2 by sheets built, step 3 by
   *  formulas calculated; null = not counted yet. The panel folds it into the overall percent. */
  pct: number | null;
  /** Step 3 before its first count: the spreadsheet is reading every formula and working out which needs which
   *  (Univer reports no progress for that), so the bar rests at 80% — the label says so rather than look stuck
   *  (user 2026-10-01). */
  preparing?: boolean;
}

export function SdmsSheetPreparing({ stage, pct, preparing = false }: SdmsSheetPreparingProps) {
  // Only ever forward (the calculation's passes each count from 0 again), and never 100% while the panel is
  // still up — it lifts the moment the sheet is ready, so "100%" would only ever be seen as a stall.
  const shownRef = useRef(0);
  const overall = Math.min(99, Math.max(shownRef.current, overallPct(stage, pct)));
  shownRef.current = overall;
  return (
    <div className="sdms-prep" role="status" aria-live="polite" aria-busy="true">
      <div className="sdms-prep-min">
        <LoaderSymbol size={34} />
        <div className="sdms-prep-title">{stage === 'calc' && preparing ? 'Preparing formulas' : STAGE_LABEL[stage]}… {overall}%</div>
        <div className="sdms-prep-sub">Step {STAGE_NO[stage]} of 3 · please wait</div>
        <div className="sdms-prep-bar" role="progressbar" aria-label="Preparing the sheet"
          aria-valuemin={0} aria-valuemax={100} aria-valuenow={overall}>
          <span style={{ width: `${overall}%` }} />
        </div>
      </div>
    </div>
  );
}

/** The small "Recalculating…" chip shown over the sheet while a recalculation after the load runs. */
export function SdmsRecalcChip({ pct }: { pct: number | null }) {
  return (
    <div className="sdms-recalc-chip" role="status" aria-live="polite">
      <LoaderSymbol size={14} />
      <span>Recalculating…{pct != null ? ` ${pct}%` : ''}</span>
    </div>
  );
}
